/**
 * Consumer Brands Feed — iter 48 · Phase C
 * ONE card per storefront (§28-30). Renders on /brands and as a strip on
 * the shopper Home + top of /coupons.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Store, Sparkles, Loader2, Heart } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function BrandsFeed({ limit = 24, compact = false }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favs, setFavs] = useState(new Set());
  useEffect(() => {
    api.get(`/v1/storefronts?limit=${limit}`).then((r) => setRows(r.data.items || []))
      .finally(() => setLoading(false));
  }, [limit]);
  // Only fetch favourites when the shopper is signed in — silences the
  // ambient 401 noise anon visitors used to see in devtools.
  useEffect(() => {
    if (!user) { setFavs(new Set()); return; }
    api.get("/v1/me/favorite-storefronts")
      .then((r) => setFavs(new Set((r.data.items || []).map((s) => s.id))))
      .catch(() => {});
  }, [user]);

  const toggleFav = async (sfId, isFav) => {
    if (!user) { toast.error("Sign in to save favourite brands."); return; }
    // Optimistic toggle
    setFavs((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(sfId); else next.add(sfId);
      return next;
    });
    try {
      if (isFav) await api.delete(`/v1/storefronts/${sfId}/favorite`);
      else       await api.post(`/v1/storefronts/${sfId}/favorite`);
    } catch {
      // Revert optimistic state so UI matches server truth
      setFavs((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(sfId); else next.delete(sfId);
        return next;
      });
      toast.error("Couldn't update favourite. Please try again.");
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-slate-500 flex items-center gap-2" data-testid="brands-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading brands…
    </div>;
  }
  if (rows.length === 0) {
    return <div className="p-6 text-sm text-slate-500" data-testid="brands-empty">No live brands yet — check back soon.</div>;
  }
  return (
    <div className={compact
        ? "grid grid-cols-2 md:grid-cols-4 gap-3"
        : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"}
         data-testid="brands-grid">
      {rows.map((sf) => (
        <BrandCard key={sf.id} sf={sf} compact={compact}
                    favorited={favs.has(sf.id)}
                    onToggleFav={() => toggleFav(sf.id, favs.has(sf.id))} />
      ))}
    </div>
  );
}

export function BrandCard({ sf, compact = false, favorited = false, onToggleFav }) {
  return (
    <Link to={`/store/${sf.slug || sf.id}`}
          className="pf-card overflow-hidden group hover:shadow-lg transition block relative"
          data-testid={`brand-card-${sf.id}`}>
      {onToggleFav && (
        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFav(); }}
                 className={`absolute top-2 right-2 z-10 w-8 h-8 rounded-full flex items-center justify-center backdrop-blur-md transition
                   ${favorited ? "bg-rose-500 text-white" : "bg-white/80 text-slate-600 hover:bg-white"}`}
                 data-testid={`brand-fav-${sf.id}`}
                 aria-label={favorited ? "Unfavourite brand" : "Favourite brand"}>
          <Heart className={`w-4 h-4 ${favorited ? "fill-current" : ""}`} />
        </button>
      )}
      <div className="h-24 relative overflow-hidden"
           style={{ backgroundImage: sf.banner_image ? `url(${sf.banner_image})` : undefined,
                    backgroundSize: "cover", backgroundPosition: "center",
                    background: sf.banner_image ? undefined : "linear-gradient(135deg,#0e7490 0%,#67e8f9 100%)" }}>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        {sf.logo_url && (
          <img src={sf.logo_url} alt="" className="absolute top-2 left-2 w-9 h-9 rounded-lg bg-white p-0.5 shadow"
               data-testid={`brand-logo-${sf.id}`} />
        )}
      </div>
      <div className={compact ? "p-3" : "p-4"}>
        <div className="flex items-baseline justify-between gap-2">
          <div className="font-display text-slate-900 truncate">{sf.name}</div>
          {sf.primary_category && (
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold shrink-0">{sf.primary_category}</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {sf.display_label && (
            <span className="pf-chip pf-chip-cyan text-[11px] font-semibold" data-testid={`brand-best-${sf.id}`}>
              <Sparkles className="w-3 h-3" /> {sf.display_label}
            </span>
          )}
          <span className="text-[11px] text-slate-500">{sf.active_offer_count} offer{sf.active_offer_count === 1 ? "" : "s"}</span>
        </div>
        {!compact && (
          <div className="mt-3 flex items-center justify-between text-xs">
            <span className="text-cyan-700 font-semibold group-hover:underline">View store</span>
            <ArrowRight className="w-3 h-3 text-cyan-700 group-hover:translate-x-0.5 transition" />
          </div>
        )}
      </div>
    </Link>
  );
}
